import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getKafTodayPractice, completeKafPractice, getKafStreak } from "@/lib/kaf-academy.functions";
import { LivingBackdrop } from "@/academy/engines/living/LivingBackdrop";
import { BreathingOrb } from "@/academy/engines/living/BreathingOrb";
import { Flame, Check } from "lucide-react";
import { useState } from "react";

export const Route = createFileRoute("/_authenticated/academy/kaf/daily")({
  head: () => ({ meta: [{ title: "Daily Practice — KAfrequency" }] }),
  component: DailyPage,
});

function DailyPage() {
  const qc = useQueryClient();
  const todayFn = useServerFn(getKafTodayPractice);
  const streakFn = useServerFn(getKafStreak);
  const complete = useServerFn(completeKafPractice);
  const today = useQuery({ queryKey: ["kaf", "today"], queryFn: () => todayFn() });
  const streak = useQuery({ queryKey: ["kaf", "streak"], queryFn: () => streakFn() });
  const [reflection, setReflection] = useState("");
  const mutation = useMutation({
    mutationFn: (id: string) => complete({ data: { assignmentId: id, reflection } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["kaf", "today"] });
      qc.invalidateQueries({ queryKey: ["kaf", "streak"] });
    },
  });

  const t = today.data;
  const practice = t?.practice;
  const assignment = t?.assignment;
  const completed = !!assignment?.completed_at;

  return (
    <div className="relative min-h-dvh bg-obsidian text-foreground">
      <div className="absolute inset-0 opacity-70"><LivingBackdrop hue={44} density={90} /></div>
      <section className="relative z-10 mx-auto max-w-3xl px-4 py-12 sm:px-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold">Today's practice</p>
            <h1 className="mt-2 font-serif text-4xl text-gold-luminous">The Daily Field</h1>
          </div>
          <div className="rounded-full border hairline px-4 py-2 text-sm">
            <Flame size={14} className="mr-1 inline text-gold" />
            {streak.data?.current_streak ?? 0} day streak
          </div>
        </div>

        {today.isLoading && <p className="mt-8 text-sm text-foreground/60">Drawing today's practice…</p>}
        {practice && (
          <div className="mt-8 rounded-2xl border hairline bg-obsidian/70 p-6">
            <div className="text-xs uppercase tracking-widest text-gold">{practice.kind}</div>
            <h2 className="mt-2 font-serif text-2xl text-gold-luminous">{practice.title}</h2>
            {practice.prompt && <p className="mt-3 text-foreground/85">{practice.prompt}</p>}
            {practice.body && <p className="mt-2 text-sm text-foreground/70">{practice.body}</p>}
            {practice.kind === "breath" && (
              <div className="mt-6"><BreathingOrb pattern="box" cycles={6} /></div>
            )}
            <textarea
              value={reflection}
              onChange={(e) => setReflection(e.target.value)}
              placeholder="A short reflection (optional)…"
              className="mt-6 min-h-[100px] w-full rounded-lg border hairline bg-obsidian/60 p-3 text-sm outline-none focus:border-gold/60"
              disabled={completed}
            />
            <div className="mt-4 flex items-center gap-3">
              {completed ? (
                <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/50 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-200">
                  <Check size={14} /> Practiced
                </span>
              ) : (
                <button
                  onClick={() => assignment && mutation.mutate(assignment.id)}
                  disabled={mutation.isPending}
                  className="rounded-full bg-gold-gradient px-5 py-2 text-sm font-semibold text-obsidian disabled:opacity-60"
                >
                  {mutation.isPending ? "Sealing…" : "Complete practice"}
                </button>
              )}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
